import { Avatar, makeStyles } from '@material-ui/core';
import classNames from 'classnames';
import PropTypes from 'prop-types';
import queryString from 'query-string';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { useCurrentViewPropertySession } from 'src/hooks/use-current-view-property-session';
import Gender from '~components/gender';
import Typography from '~components/typography';
import { PortraitDefault } from '~svg-components';
import hexToRGBA from '~utils/hex-to-rgba';
import ExternalIdChip from './external-id-chip';

const useStyles = makeStyles(
  (theme) => ({
    root: {
      display: 'flex',
      alignItems: 'stretch',
      width: '100%',
      minWidth: '348px',
      padding: '10px 12px 8px 16px',
      minHeight: '72px',
      backgroundColor: theme.colors.white,
      borderRadius: theme.borderRadius.small,
      border: `1px solid ${theme.colors.greyE8}`,
      cursor: 'pointer',
      marginBottom: '12px',
      '&:hover': {
        backgroundColor: theme.colors.greyFa,
      },
    },

    avatar: {
      width: 40,
      height: 40,
      marginRight: 12,
      marginTop: 5,
    },

    avatarFile: {
      width: 'inherit',
      height: 'inherit',
    },

    avatarBorder: {
      border: `1px solid ${hexToRGBA(theme.colors.grey66, 0.16)}`,
    },

    defaultAvatar: {
      width: '100%',
      height: '100%',
    },

    nameGenderEmail: {
      flex: 1,
    },

    nameGender: {
      wordBreak: 'break-all',
      marginBottom: 4,
      paddingRight: 12,
    },

    contactEmail: {
      wordBreak: 'break-all',
    },

    accountUnverified: {
      backgroundColor: theme.colors.orangeBg,
      borderRadius: theme.borderRadius.huge,
      padding: '0 8px',
      justifyContent: 'flex-end',
      marginTop: 2,
    },

    accountUnverifiedRebooker: {
      backgroundColor: theme.colors.roseBg,
    },

    externalId: {
      ...theme.flex.crossStart,
    },

    externalIdLabel: {
      ...theme.typography.body2,
      width: 79,
      color: theme.colors.greyCc,
      marginRight: 8,
    },

    externalIdContent: {
      ...theme.typography.body2,
      color: theme.colors.white,
    },

    renderExternalId: {
      marginTop: 4,
    },

    tagAndChip: {
      ...theme.flex.col,
      justifyContent: 'space-between',
      alignItems: 'flex-end',
      minWidth: '60px',
    },

    chip: {
      width: '100%',
      marginBottom: 4,
    },
  }),
  { name: 'StudentCard' }
);

function StudentCard({ student }) {
  const { t } = useTranslation('global-search');
  const classes = useStyles();

  const {
    fullName,
    gender,
    contactEmail,
    emailVerified,
    isRebooker,
    studentId,
    originId,
    customFieldValues,
  } = student;

  const [
    {
      currentViewProperty: { id: propertyId },
    },
  ] = useCurrentViewPropertySession();

  const openStudentDetailPage = () => {
    const studentDetailsUrl = queryString.stringifyUrl({
      url: '/student/student-details/personal-information',
      query: { studentId, propertyId },
    });
    window.open(studentDetailsUrl);
  };

  const renderStudentAvatar = () => {
    const src = student.profilePhotoFileUuid;
    const avatarSrc = src ? `${process.env.REACT_APP_FILE_SERVICE_URL}/files/${src}` : undefined;
    return (
      <div className={classes.avatar}>
        <Avatar
          src={avatarSrc}
          className={classNames(classes.avatarFile, {
            [classes.avatarBorder]: avatarSrc,
          })}
        >
          <PortraitDefault className={classes.defaultAvatar} />
        </Avatar>
      </div>
    );
  };

  const renderStudentNameGenderEmail = () => (
    <div className={classes.nameGenderEmail}>
      <Typography variant="body1" themeColor="dark" className={classes.nameGender}>
        {fullName} <Gender type={gender} style={{ verticalAlign: 'middle' }} />
      </Typography>
      <Typography variant="body2" themeColor="grey66" className={classes.contactEmail}>
        {contactEmail}
      </Typography>
    </div>
  );

  const renderAccountUnverifiedRebookerTagAndChip = () => (
    <div className={classes.tagAndChip}>
      <Typography
        variant="body2"
        themeColor={isRebooker ? 'roseDark' : 'orangeDark'}
        className={classNames(classes.accountUnverified, {
          [classes.accountUnverifiedRebooker]: isRebooker,
        })}
      >
        {isRebooker && t('rebooker')}
        {!emailVerified && !isRebooker && t('unverified')}
      </Typography>
      <ExternalIdChip className={classes.chip} externalIds={customFieldValues} cardId={originId} />
    </div>
  );

  return (
    <div
      className={classes.root}
      onClick={() => {
        openStudentDetailPage();
      }}
    >
      {renderStudentAvatar()}
      {renderStudentNameGenderEmail()}
      {renderAccountUnverifiedRebookerTagAndChip()}
    </div>
  );
}

StudentCard.prototype = {
  student: PropTypes.object.isRequired,
};

export default StudentCard;
